'use client';

import { useEffect, useRef, useState, type ReactNode } from 'react';

type DeferUntilVisibleProps = {
  /** Content rendered once the placeholder scrolls near the viewport. */
  children: ReactNode;
  /** Distance from the viewport that triggers mounting. Defaults to 480px. */
  rootMargin?: string;
  /** Reserves vertical space so deferred mount does not cause layout shift. */
  minHeight?: number | string;
};

export function DeferUntilVisible({
  children,
  rootMargin = '480px',
  minHeight,
}: DeferUntilVisibleProps) {
  const placeholderRef = useRef<HTMLDivElement | null>(null);
  const [shouldRender, setShouldRender] = useState(false);

  useEffect(() => {
    const node = placeholderRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setShouldRender(true);
            observer.disconnect();
            break;
          }
        }
      },
      { rootMargin },
    );
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [rootMargin]);

  if (shouldRender) {
    return <>{children}</>;
  }

  return <div ref={placeholderRef} style={{ minHeight }} aria-hidden />;
}
